import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../redux/reducers/index";
import config from "../config/enviroment";
import { shouldShowMarcaBlancaModal } from "../components/MarcaBlancaModal/shouldShow";
import { getHashFromUrl, removeHashFromUrl } from "../utils/marcaBlancaHash";
import useMarcaBlancaAuth from "./useMarcaBlancaAuth";
import useMarcaBlancaVerify from "./useMarcaBlancaVerify";
import {
  setMarcaBlancaValidated,
  setMarcaBlancaClient,
  setMarcaBlancaHashValidating,
  setMarcaBlancaHashFailed,
  resetMarcaBlancaHash,
} from "../redux/actions/marcaBlancaActions";

const useMarcaBlancaHashAuth = () => {
  const dispatch = useDispatch();
  const clientState: any = useSelector((state: RootState) => state.client);
  const marcaBlanca = useSelector((state: RootState) => state.marcaBlanca);
  const { authenticateByHash } = useMarcaBlancaAuth();
  const { verify } = useMarcaBlancaVerify();

  const attemptedHashRef = useRef<string | null>(null);

  const gated = shouldShowMarcaBlancaModal(
    clientState?.id,
    config.marcaBlancaId
  );
  const hash = getHashFromUrl();

  useEffect(() => {
    if (!gated || !hash) return;
    if (marcaBlanca.validated) return;
    if (attemptedHashRef.current === hash) return;
    attemptedHashRef.current = hash;

    let cancelled = false;

    const run = async () => {
      dispatch(setMarcaBlancaHashValidating());
      const result = await authenticateByHash(hash);
      if (cancelled) return;

      const data: any = result?.data;
      const credentials = {
        clientId: String(data?.clientId ?? ""),
        documentNumber: String(data?.documentNumber ?? ""),
        documentTypeId: data?.documentTypeId ?? "",
      };

      // Validamos el hash contra /v2/white-mark/lookup (enviándolo en el body).
      const { ok, hashInvalid } = await verify({ ...credentials, hash });
      if (cancelled) return;

      // Hash inexistente/expirado: NO mostramos el modal de error ("usuario no
      // encontrado"). Quitamos el hash de la URL y reseteamos su estado para
      // que el guardia deje de tratar la ruta como "autenticando por hash" y
      // redirija al home a pedir la autenticación manual (igual que sin hash).
      if (hashInvalid) {
        removeHashFromUrl();
        dispatch(resetMarcaBlancaHash());
        return;
      }

      if (result?.data && ok) dispatch(setMarcaBlancaClient(result));

      if (data?.status) {
        dispatch(setMarcaBlancaValidated(credentials));
        dispatch(resetMarcaBlancaHash());
      } else {
        dispatch(setMarcaBlancaHashFailed(result));
      }
    };

    run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gated, hash, marcaBlanca.validated]);
};

export default useMarcaBlancaHashAuth;
